/**
 * Define user global data fetched from the backend.
 *
 * Copyright © Ircam Amplify 2024-2025
 */

import { fetchAuthSession } from 'aws-amplify/auth'
import axios from 'axios'
import {
  FIELD_START_DATETIME,
  SORT_ASCENDING,
} from '../components/TaskList/JobListDef'
import {
  setAllowedCountries,
  setAppClients,
  setCompanyConsumption,
  setCompanyKpi,
  setJobs,
  setLastJobId,
  setLastWebhookLogId,
  setServicesInfo,
  setUser,
  setWebhookLogs,
  setWebhooks,
  store,
} from '../store'
import { isDebugMockup } from '../utils/TestUtil'
import { API } from './API'
import { LogTag } from '@amplify3/duplo-back-end'
import { logger } from '@/config/logger'

export const fetchUser = async () => {
  const authSession = await fetchAuthSession()
  const { data } = await API(authSession).get('/user')
  store.dispatch(setUser(data?.user))
}

export const fetchCompanyConsumption = async () => {
  store.dispatch(setCompanyConsumption(undefined))
  const authSession = await fetchAuthSession()
  const { data } = await API(authSession).get('/company/consumption')
  logger.debug(LogTag.Util, '/company/consumption response:', data)
  store.dispatch(setCompanyConsumption(data.consumption))
}

export const fetchCompanyKpi = async () => {
  store.dispatch(setCompanyKpi(undefined))
  const authSession = await fetchAuthSession()

  let data

  if (isDebugMockup()) {
    // load a mock-up of API response for debug and tests
    const mock = await import('../test/company_kpi_mockup.json')
    data = mock.default
  } else {
    // Request KPI from the backend
    const response = await API(authSession).get('/company/kpis')
    data = response.data
  }

  if (isDebugMockup()) {
    // Warn the use of mock-up in the logs
    logger.warning(
      LogTag.Util,
      'Use test mockup for /company/kpis response:',
      data
    )
  } else {
    logger.debug(LogTag.Util, '/company/kpis response:', data)
  }
  store.dispatch(setCompanyKpi(data?.kpis))
}

export const fetchJobs = async ({ refreshing }: { refreshing: boolean }) => {
  const { jobs, lastJobId, user, jobsSortDirection } = store.getState().user
  const authSession = await fetchAuthSession()
  const sortDir = jobsSortDirection === SORT_ASCENDING ? 'asc' : 'desc'
  const lastJobParam =
    !refreshing && lastJobId?.length ? `&last_job_id=${lastJobId}` : ''

  const url =
    `/company/jobs?client_id=${user?.user_cognito_id}` +
    `&limit=10` +
    `&sort=["${FIELD_START_DATETIME}", "${sortDir}"]` +
    lastJobParam +
    `&reserved=with_cost_info`

  const { data } = await API(authSession).get(url)

  const jobsToSave = refreshing
    ? (data?.jobs ?? [])
    : [
        ...new Map(
          [...jobs, ...(data?.jobs ?? [])].map((item) => [item?.job_id, item])
        ).values(),
      ]
  const sortedJobs =
    jobsSortDirection === SORT_ASCENDING
      ? jobsToSave.sort(
          (a: any, b: any) =>
            new Date(a?.job_start_datetime ?? 0).getTime() -
            new Date(b?.job_start_datetime ?? 0).getTime()
        )
      : jobsToSave.sort(
          (a: any, b: any) =>
            new Date(b?.job_start_datetime ?? 0).getTime() -
            new Date(a?.job_start_datetime ?? 0).getTime()
        )

  store.dispatch(setJobs(sortedJobs))
  store.dispatch(setLastJobId(data?.last_job_id))
}

export const fetchServicesInfo = async () => {
  const authSession = await fetchAuthSession()
  const { data } = await API(authSession).get('/services-info')
  store.dispatch(setServicesInfo(data))
}

export const fetchApiCreds = async () => {
  const authSession = await fetchAuthSession()
  const { data } = await API(authSession).get('/app')
  store.dispatch(setAppClients(data?.app_clients))
}

export const fetchWebhooks = async () => {
  const authSession = await fetchAuthSession()
  const { data } = await API(authSession).get('/webhook')
  store.dispatch(setWebhooks(data?.webhooks ?? []))
}

export const fetchWebhookLogs = async ({
  webhookId,
  refreshing,
}: {
  webhookId: string
  refreshing: boolean
}) => {
  const { webhookLogs, webhookLogsSortDirection, lastWebhookLogId } =
    store.getState().user
  const authSession = await fetchAuthSession()

  const { data } = await API(authSession).get(
    `webhook/${webhookId}/log?limit=10&sort=["date", "${webhookLogsSortDirection === SORT_ASCENDING ? 'asc' : 'desc'}"]${!refreshing && lastWebhookLogId !== undefined && lastWebhookLogId?.length > 0 ? `&last_log_id=${lastWebhookLogId}` : ''}`
  )

  const logsToSave = refreshing
    ? (data?.webhook_logs ?? [])
    : [
        ...new Map(
          [...(webhookLogs ?? []), ...(data?.webhook_logs ?? [])].map(
            (item) => [item?.id, item]
          )
        ).values(),
      ]
  const sortedLogs =
    webhookLogsSortDirection === SORT_ASCENDING
      ? logsToSave.sort(
          (a: any, b: any) =>
            new Date(a?.date ?? 0).getTime() - new Date(b?.date ?? 0).getTime()
        )
      : logsToSave.sort(
          (a: any, b: any) =>
            new Date(b?.date ?? 0).getTime() - new Date(a?.date ?? 0).getTime()
        )

  store.dispatch(setWebhookLogs(sortedLogs))
  store.dispatch(setLastWebhookLogId(data?.last_log_id))
}

export const fetchAllowedCountries = async () => {
  const { data } = await axios.create().get('/allowedCountries.json')
  store.dispatch(setAllowedCountries(data ?? []))
}
